import { storeConfig } from '@/shared/storeConfig';

export const useSmarticoConfigs = defineStore(
  'smarticoConfigs',
  () => {
    const configSmartico = useRuntimeConfig().public.smartico;

    const gamificationName = ref<string>(
      configSmartico.gamificationName || 'Club Vip',
    );

    const gamificationCoinName = ref<string>(
      configSmartico.gamificationCoinName || 'Coin',
    );

    const gamificationCoinImageUrl = ref<string>(
      getAssetsUrlForCDN(`/assets/gamification/coin.png`),
    );

    const { userId } = useUserData();

    const { visitorOrUserCountry } = useParameters();

    const isSmarticoActived = ref<boolean>(
      !!configSmartico.brandId && !!configSmartico.labelId,
    );

    const isSmarticoVisitorModeActived = ref<boolean>(
      Boolean(
        String(
          configSmartico.visitorBrandId || configSmartico.brandId || '',
        ).trim() &&
          String(
            configSmartico.visitorLabelId || configSmartico.labelId || '',
          ).trim(),
      ),
    );

    const isGamificationActived = ref<boolean>(
      !!configSmartico.isGamificationActive && !!isSmarticoActived.value,
    );

    const gamificationActiveAndShow = computed(() => {
      if (!isGamificationActived.value) {
        return false;
      }

      const usersMasterTesters =
        configSmartico.gamificationMasterTesters.split(',');

      if (usersMasterTesters.includes(String(userId.value))) {
        return true;
      }

      if (configSmartico.gamificationSpecificUsers) {
        const usersInWhiteList =
          configSmartico.gamificationSpecificUsers.split(',');

        return usersInWhiteList.includes(String(userId.value));
      }

      if (configSmartico.gamificationSpecificCountries) {
        const allowedCountries =
          configSmartico.gamificationSpecificCountries.split(',');

        return allowedCountries.includes(String(visitorOrUserCountry.value));
      }

      return true;
    });

    const isGamificationTournamentsActived = computed(() => {
      if (!gamificationActiveAndShow.value) {
        return false;
      }
      if (!configSmartico.gamificationModules.tournaments) {
        return false;
      }

      return true;
    });

    const gamificationExclusiveTournaments = ref(
      configSmartico.exclusiveTournaments.split(','),
    );

    const isGamificationMissionsActived = computed(() => {
      if (!gamificationActiveAndShow.value) {
        return false;
      }
      if (!configSmartico.gamificationModules.missions) {
        return false;
      }

      return true;
    });

    const isGamificationStoreActived = computed(() => {
      if (!gamificationActiveAndShow.value) {
        return false;
      }
      if (!configSmartico.gamificationModules.store) {
        return false;
      }

      return true;
    });

    const isGamificationMiniGamesActived = computed(() => {
      if (!gamificationActiveAndShow.value) {
        return false;
      }
      if (!configSmartico.gamificationModules.minigames) {
        return false;
      }

      return true;
    });

    const isGamificationLevelsActived = computed(() => {
      if (!gamificationActiveAndShow.value) {
        return false;
      }
      if (!configSmartico.gamificationModules.levels) {
        return false;
      }

      return true;
    });

    const isGamificationProfileActived = computed(() => {
      if (!gamificationActiveAndShow.value) {
        return false;
      }
      if (!configSmartico.gamificationModules.profile) {
        return false;
      }

      return true;
    });

    const isGamificationNativeActived = computed(() => {
      if (!configSmartico.isGamificationNativeActive) {
        return false;
      }
      if (!isSmarticoActived.value) {
        return false;
      }

      if (configSmartico.gamificationSpecificUsers) {
        const usersInWhiteList =
          configSmartico.gamificationSpecificUsers.split(',');

        return usersInWhiteList.includes(String(userId.value));
      }

      return true;
    });

    const isGamificationNativeTournamentsActived = computed(() => {
      if (!isGamificationNativeActived.value) {
        return false;
      }
      if (!configSmartico.gamificationNativeModules.tournaments) {
        return false;
      }

      return true;
    });

    const isGamificationNativeMissionsActived = computed(() => {
      if (!isGamificationNativeActived.value) {
        return false;
      }
      if (!configSmartico.gamificationNativeModules.missions) {
        return false;
      }

      return true;
    });

    const isRequestPushPermission = computed(() => {
      if (!configSmartico.requestPushNotification) {
        return false;
      }
      if (!isSmarticoActived.value) {
        return false;
      }

      return true;
    });

    const isNotificationActived = computed(() => {
      if (!configSmartico.activeNotifications) {
        return false;
      }
      if (!isSmarticoActived.value) {
        return false;
      }

      return true;
    });

    const isAllowLocalhost = ref<boolean>(configSmartico.allowLocalhost);

    const activeSmarticoDebugs = ref<boolean>(configSmartico.activeDebugs);

    function debugLog(...args: any[]) {
      if (activeSmarticoDebugs.value) {
        console.debug(...args);
      }
    }

    return {
      configSmartico,
      isSmarticoActived,
      isSmarticoVisitorModeActived,
      isGamificationActived,
      gamificationActiveAndShow,
      isGamificationTournamentsActived,
      isGamificationMissionsActived,
      isGamificationStoreActived,
      isGamificationMiniGamesActived,
      isGamificationLevelsActived,
      isGamificationProfileActived,
      isGamificationNativeActived,
      isGamificationNativeTournamentsActived,
      isGamificationNativeMissionsActived,
      gamificationName,
      gamificationCoinName,
      gamificationExclusiveTournaments,
      gamificationCoinImageUrl,
      isNotificationActived,
      isRequestPushPermission,
      isAllowLocalhost,
      debugLog,
    };
  },

  storeConfig,
);
